import { useMemo } from 'react'

import {
  useAdaptivePollingConfig,
  type UseAdaptivePollingConfigDetailResult
} from './use_adaptive_polling_config'

export interface UseLiveMeetingDetailPollingParams {
  /** When true (e.g. flag + ?live=true), detail polling is enabled. When false, no polling. */
  enabled: boolean
  /** When true, use relaxed polling (e.g. 60s); when false, use aggressive (e.g. 5s). From useSSEConnectedForPolling() when available. */
  isSSEConnected?: boolean
  eventId: string | undefined
  shareId: string | undefined
  /** True when the selected meeting is non-Calendly (external); required for GraphQL polling decision. */
  isExternalEvent: boolean
  selectedView: string
}

export interface GraphQLPollingQueryArgs {
  uuid: string
}

export interface LiveMeetingDetailPollingResult {
  /** Polling config for REST getMeeting (and for GraphQL getMeeting when external). */
  detailPollingConfig: UseAdaptivePollingConfigDetailResult['detailPollingConfig']
  /** When true, the consumer should run a polling GraphQL getMeeting query for the selected external meeting. */
  shouldPollGraphQLForExternal: boolean
  /** Query args for the GraphQL getMeeting call when shouldPollGraphQLForExternal is true; empty object otherwise. */
  graphQLPollingQueryArgs: GraphQLPollingQueryArgs | Record<string, never>
}

/**
 * Orchestrates detail (right rail) polling for the meetings list page.
 * When enabled (live=true), returns detail polling config and GraphQL polling decision for external meetings.
 * Consumer (e.g. MeetingDetailProvider) passes isSSEConnected from useSSEConnectedForPolling().
 */
export function useLiveMeetingDetailPolling(
  params: UseLiveMeetingDetailPollingParams
): LiveMeetingDetailPollingResult {
  const {
    enabled,
    isSSEConnected = false,
    eventId,
    shareId,
    isExternalEvent,
    selectedView,
  } = params

  const { detailPollingConfig } = useAdaptivePollingConfig({
    isSSEConnected,
    variant: 'detail',
    enabled,
  })

  const shouldPollGraphQLForExternal = Boolean(
    eventId && !shareId && isExternalEvent && selectedView !== 'pending' && enabled
  )

  const graphQLPollingQueryArgs = useMemo(() => {
    if (!shouldPollGraphQLForExternal || !eventId) return {} as Record<string, never>
    return { uuid: eventId }
  }, [shouldPollGraphQLForExternal, eventId])

  return useMemo(
    () => ({
      detailPollingConfig,
      shouldPollGraphQLForExternal,
      graphQLPollingQueryArgs,
    }),
    [detailPollingConfig, shouldPollGraphQLForExternal, graphQLPollingQueryArgs]
  )
}
